import { Reveal } from "./Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const forYouItems = [
  "You've done the inner work and you want it to reach your child, not stop with you.",
  "You're excited to look at your default words with curiosity, not judgment.",
  "You want to understand why your words land the way they do, not just get a script.",
  "You're ready to be the mom the cycle ends with.",
];

const notForYouItems = [
  "You're looking for a quick fix. This is a six-month partnership, and it starts with an honest look at your own words.",
  "You want someone to manage tantrums in the moment. We build the beliefs underneath, so calm and confidence last.",
  "You're not ready to look at how your own childhood shows up in your words. That's okay, it just may not be this season.",
];

const faqs = [
  {
    q: "Is this therapy?",
    a: "No. LLA is parent coaching. We focus on the words you say in everyday moments, not on processing your past.",
  },
  {
    q: "I've already done years of inner work. Why would I need this?",
    a: "Inner work changes what you believe. This changes what comes out of your mouth when you're tired — and that's what your child hears.",
  },
  {
    q: "Will this make me permissive?",
    a: "The opposite. Most moms swing between their mother's voice and too much gentleness. You'll find a third way: warm, calm boundaries you don't have to defend.",
  },
  {
    q: "My child is already 4. Is it too late?",
    a: "No. Children's language shifts quickly when the words around them change. Starting now means the rest of these early years are built on the new words.",
  },
  {
    q: "Do I need extra time each day?",
    a: "No. This happens inside what you already do: store runs, bath time, bedtime.",
  },
  {
    q: "What if I'm not sure it's a fit?",
    a: "Apply, and we'll talk it through on your fit call. Applying doesn't enroll you or charge you.",
  },
];

const CheckIcon = () => (
  <svg className="mt-1.5 w-5 h-5 shrink-0 text-[#8C4A32]" viewBox="0 0 20 20" fill="none">
    <path d="M4 10.5L8.5 15L16 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg className="mt-1.5 w-5 h-5 shrink-0 text-[#8A7E74]" viewBox="0 0 20 20" fill="none">
    <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const listHeadingClass =
  "font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#241E1A] font-medium";

export const ForYouAndFaq = () => (
  <>
    <section data-testid="for-you-section" className="py-16 sm:py-24 lg:py-32 bg-[#F4EFE6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4">Fit</p>
            <h2 className={listHeadingClass}>This is for you if…</h2>
          </Reveal>
          <ul className="mt-8 space-y-5">
            {forYouItems.map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li data-testid={`fit-item-${i + 1}`} className="flex gap-4 items-start text-base sm:text-lg leading-relaxed text-[#241E1A]">
                  <CheckIcon />
                  {f}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
        <div className="md:col-span-6">
          <Reveal>
            <h2 className={`${listHeadingClass} md:pt-9`}>This isn't for you if…</h2>
          </Reveal>
          <ul className="mt-8 space-y-5">
            {notForYouItems.map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li data-testid={`not-fit-item-${i + 1}`} className="flex gap-4 items-start text-base sm:text-lg leading-relaxed text-[#5C524A]">
                  <XIcon />
                  {f}
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.35}>
            <p className="mt-10 font-serif-display italic text-xl sm:text-2xl text-[#5C524A] leading-snug">
              LLA isn't quick tips or sticker charts. It's for the mom who wants her words to carry who she's become.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="faq-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4 text-center">Questions</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#241E1A] font-medium text-center">
            Everything you're wondering
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-accordion-item-${i + 1}`} className="border-[#E6DEC3]">
                <AccordionTrigger className="text-left font-serif-display text-lg sm:text-xl text-[#241E1A] hover:text-[#8C4A32] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-[#5C524A] pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  </>
);
